import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBankDetailsForm } from './useBankDetailsForm';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountType, RoutePaths } from 'Constants/Common';
import { useAuth0 } from '@auth0/auth0-react';
import { PaymentApi } from 'api/PaymentApi';
import { BankDetailType } from '../../common/CommonTypes';
import ToggleButton from '../../common/toggleButton/ToggleButton';
import FormInput from '../../common/FormInput';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import PageTitle from '../../common/PageTitle';
import BankDetailsForm from './BankDetailsForm';
import BankDetailsInfo from 'components/payment/BankDetailsInfo';
import Button from '../../common/button/Button';

const RepaymentSetupPage = () => {
    const { t } = useTranslation();
    const [, setValidated] = useState(false);
    const [showBankInputSection, setShowBankInputSection] = useState(false);
    const [bankDetailsState, setBankDetailsState] = useState({
        accountHolderName: '',
        sortCode: [
            { key: '', value: '' },
            { key: '', value: '' },
            { key: '', value: '' }
        ], // array of 3 parts
        accountNumber: '',
        paymentDay: '',
        businessPartnerId: 0
    });
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const location = useLocation();

    const policyReferenceNumber =
        location.state ?? ({} as { referenceNumber: string });

    const {
        scrollDivRef,
        bankDetails,
        bankDetailErrors,
        daysOptions,
        onChange,
        onSortCodeChange,
        onPaymentDayChange,
        getErrors,
        onSubmit,
        onYesButtonClick,
        isConfirmed,
        setIsConfirmed,
        confirmationError,
        setConfirmationError
    } = useBankDetailsForm({
        onSuccess: () => {
            setValidated(true);
            toast.success(t('repayment_setup.validate-bank-success'), {
                onClose: () => {
                    navigate(RoutePaths.ViewRequests);
                }
            });
        },
        t,
        showPaymentDay: false,
        accountType: AccountType.Disbursement,
        referenceNumber: policyReferenceNumber?.referenceNumber,
        businessPartnerId: bankDetailsState.businessPartnerId,
        includeConfirmationValidation: showBankInputSection
    });

    useEffect(() => {
        loadRepaymentBankDetails();
    }, [policyReferenceNumber]);

    const loadRepaymentBankDetails = async () => {
        const successCallback = (result: BankDetailType) => {
            if (result.accountNumber && result.accountNumber !== '') {
                setBankDetailsState({
                    accountHolderName: result.accountHolderName || '',
                    accountNumber: result.accountNumber || '',
                    sortCode: (result.sortCode
                        ? result.sortCode.split('-')
                        : ['', '', '']
                    ).map((part) => ({ key: part, value: part })),
                    paymentDay: '',
                    businessPartnerId: result.businessPartnerId
                });
                setShowBankInputSection(false);
            } else {
                setShowBankInputSection(true);
            }
        };

        const failureCallback = () => {
            console.log('Failed to load direct debit details');
        };

        const token = await getAccessTokenSilently();

        new PaymentApi().GetDirectDebit(
            policyReferenceNumber?.referenceNumber,
            token,
            successCallback,
            failureCallback,
            new AbortController().signal
        );
    };

    const onBack = () => {
        navigate(RoutePaths.ViewRequests);
    };
    const onNoButtonClick = () => {
        setShowBankInputSection(true);
    };

    const handleToggleChange = (value: boolean) => {
        setIsConfirmed(value);
        if (value) {
            setConfirmationError('');
        }
    };

    const renderConfirmationToggle = () => {
        if (!showBankInputSection) return null;

        return (
            <div className='card-body page-content-body'>
                <FormInput
                    id='bank-confirmation-toggle'
                    errorText={confirmationError}>
                    <div className='col'>
                        <ToggleButton
                            value={isConfirmed}
                            onChange={handleToggleChange}
                            label={t(
                                'repayment_setup.toggle_confirmation_label'
                            )}
                        />
                    </div>
                </FormInput>
            </div>
        );
    };

    return (
        <div className='card page-content' ref={scrollDivRef}>
            <PageTitle title={t('repayment_bank_details_title')} />

            <p className='p-3 mb-0' style={{ whiteSpace: 'pre-wrap' }}>
                {t('repayment_bank_details_instruction')}
            </p>

            {bankDetailsState.accountNumber && (
                <>
                    <p className='p-3 mb-0'>
                        {t('repayment_bank_details_collection')}
                    </p>
                    <BankDetailsInfo
                        accountName={bankDetailsState.accountHolderName}
                        accountNumber={bankDetailsState.accountNumber}
                        sortCode={bankDetailsState.sortCode
                            .map((v) => v.value)
                            .join()}
                        className='m-3'
                        minimized
                    />
                </>
            )}
            {showBankInputSection && (
                <BankDetailsForm
                    bankDetails={bankDetails}
                    bankDetailErrors={bankDetailErrors}
                    daysOptions={daysOptions}
                    t={t}
                    onChange={onChange}
                    onSortCodeChange={onSortCodeChange}
                    onPaymentDayChange={onPaymentDayChange}
                    showPaymentDay={false}
                />
            )}

            {renderConfirmationToggle()}

            <div className='card-footer page-content-footer'>
                <ErrorSummary
                    title={t('page_errors')}
                    errors={getErrors()}
                    className='card-error'
                />
            </div>

            {showBankInputSection ? (
                <div className='bottom-navigation'>
                    <Button
                        text={t('submit')}
                        className='btn btn-primary progress-button'
                        onClick={onSubmit}
                    />
                    <Button
                        text={t('back')}
                        className='btn btn-secondary progress-button'
                        onClick={onBack}
                    />
                </div>
            ) : (
                <div className='bottom-navigation'>
                    <Button
                        text='Yes'
                        className='btn btn-primary progress-button'
                        onClick={onYesButtonClick}
                    />
                    <Button
                        text='No'
                        className='btn btn-secondary progress-button'
                        onClick={onNoButtonClick}
                    />
                    <Button
                        text={t('back')}
                        className='btn btn-secondary progress-button'
                        onClick={onBack}
                    />
                </div>
            )}
        </div>
    );
};

export default RepaymentSetupPage;
